import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  Divider,
  List,
  ListItem,
  Avatar,
  Tabs,
  Tab
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  AssignmentInd as AssignIcon,
  CheckCircle as ResolveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useProfile } from '../../contexts/ProfileContext';
import { hasSystemPermission, SYSTEM_PERMISSIONS } from '../../utils/systemPermissions';
import {
  getAllTickets,
  getTicketById,
  getTicketStats,
  updateTicketStatus,
  updateTicketPriority,
  assignTicket,
  addTicketResponse,
  searchTickets,
  markTicketAsRead,
  TICKET_STATUS,
  TICKET_PRIORITY,
  TICKET_CATEGORY,
  getStatusDisplayName,
  getPriorityDisplayName,
  getCategoryDisplayName
} from '../../services/helpDeskService';
import { getSystemAdmins } from '../../services/userManagementService';
import { formatDistanceToNow, format } from 'date-fns';
import logger from '../../utils/logger';

const HelpDeskManagement = () => {
  const { profile } = useProfile();

  // States
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [tabValue, setTabValue] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialogs
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Admin list for assignment
  const [admins, setAdmins] = useState([]);

  // Reply form
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Alerts
  const [alert, setAlert] = useState(null);

  const canView = hasSystemPermission(profile, SYSTEM_PERMISSIONS.VIEW_USERS);
  const canManage = hasSystemPermission(profile, SYSTEM_PERMISSIONS.MANAGE_USERS);

  useEffect(() => {
    loadTickets();
    loadStats();
    loadAdmins();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterPriority, filterCategory]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterPriority !== 'all') filters.priority = filterPriority;
      if (filterCategory !== 'all') filters.category = filterCategory;

      const data = await getAllTickets(filters);
      setTickets(data);
    } catch (error) {
      logger.error('Error loading tickets:', error);
      setAlert({ severity: 'error', message: 'Failed to load tickets' });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const ticketStats = await getTicketStats();
      setStats(ticketStats);
    } catch (error) {
      logger.error('Error loading stats:', error);
    }
  };

  const loadAdmins = async () => {
    try {
      const adminList = await getSystemAdmins();
      setAdmins(adminList);
    } catch (error) {
      logger.error('Error loading admins:', error);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.trim()) {
      try {
        setLoading(true);
        const results = await searchTickets(searchTerm);
        setTickets(results);
      } catch (error) {
        logger.error('Error searching:', error);
        setAlert({ severity: 'error', message: 'Search failed' });
      } finally {
        setLoading(false);
      }
    } else {
      loadTickets();
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      const fullTicket = await getTicketById(ticket.id);
      setSelectedTicket(fullTicket);
      setViewDialogOpen(true);

      if (fullTicket.userResponsePending) {
        await markTicketAsRead(ticket.id);
      }
    } catch (error) {
      logger.error('Error loading ticket:', error);
      setAlert({ severity: 'error', message: 'Failed to load ticket' });
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateTicketStatus(ticketId, newStatus, profile.uid, profile.displayName);
      setAlert({ severity: 'success', message: 'Status updated' });

      // Refresh ticket if viewing
      if (selectedTicket?.id === ticketId) {
        const updated = await getTicketById(ticketId);
        setSelectedTicket(updated);
      }
      loadTickets();
      loadStats();
    } catch (error) {
      logger.error('Error updating status:', error);
      setAlert({ severity: 'error', message: 'Failed to update status' });
    }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    try {
      await updateTicketPriority(ticketId, newPriority, profile.uid);
      setAlert({ severity: 'success', message: 'Priority updated' });
      loadTickets();
      loadStats();
    } catch (error) {
      logger.error('Error updating priority:', error);
      setAlert({ severity: 'error', message: 'Failed to update priority' });
    }
  };

  const handleAssignTicket = async (assigneeId, assigneeName) => {
    try {
      await assignTicket(selectedTicket.id, assigneeId, assigneeName, profile.uid);
      setAlert({ severity: 'success', message: `Ticket assigned to ${assigneeName}` });
      setAssignDialogOpen(false);

      const updated = await getTicketById(selectedTicket.id);
      setSelectedTicket(updated);
      loadTickets();
    } catch (error) {
      logger.error('Error assigning ticket:', error);
      setAlert({ severity: 'error', message: 'Failed to assign ticket' });
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;

    try {
      setSubmitting(true);

      await addTicketResponse(selectedTicket.id, {
        message: replyMessage,
        authorId: profile.uid,
        authorName: profile.displayName || profile.email,
        authorEmail: profile.email,
        isAdminResponse: true
      });

      setReplyMessage('');
      setAlert({ severity: 'success', message: 'Reply sent' });

      const updated = await getTicketById(selectedTicket.id);
      setSelectedTicket(updated);
    } catch (error) {
      logger.error('Error sending reply:', error);
      setAlert({ severity: 'error', message: 'Failed to send reply' });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TICKET_STATUS.OPEN: return 'info';
      case TICKET_STATUS.IN_PROGRESS: return 'warning';
      case TICKET_STATUS.WAITING_RESPONSE: return 'secondary';
      case TICKET_STATUS.RESOLVED: return 'success';
      case TICKET_STATUS.CLOSED: return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case TICKET_PRIORITY.URGENT: return 'error';
      case TICKET_PRIORITY.HIGH: return 'warning';
      case TICKET_PRIORITY.MEDIUM: return 'info';
      default: return 'default';
    }
  };

  // Filter tickets by tab
  const getFilteredTickets = () => {
    let filtered = tickets;
    if (tabValue === 1) {
      // My Assigned
      filtered = tickets.filter(t => t.assignedTo === profile.uid);
    } else if (tabValue === 2) {
      // Unassigned
      filtered = tickets.filter(t => !t.assignedTo && t.status === TICKET_STATUS.OPEN);
    } else if (tabValue === 3) {
      // Urgent
      filtered = tickets.filter(t => t.priority === TICKET_PRIORITY.URGENT || t.priority === TICKET_PRIORITY.HIGH);
    }
    return filtered;
  };

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">You do not have permission to access the help desk</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Help Desk Management
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => { loadTickets(); loadStats(); }}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">{stats.total}</Typography>
                <Typography variant="caption">Total</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="info.main">{stats.open}</Typography>
                <Typography variant="caption">Open</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main">{stats.inProgress}</Typography>
                <Typography variant="caption">In Progress</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="secondary.main">{stats.waitingResponse}</Typography>
                <Typography variant="caption">Waiting</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main">{stats.urgent}</Typography>
                <Typography variant="caption">Urgent</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">{stats.resolved}</Typography>
                <Typography variant="caption">Resolved</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.800' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                {Object.values(TICKET_STATUS).map(s => (
                  <MenuItem key={s} value={s}>{getStatusDisplayName(s)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select value={filterPriority} label="Priority" onChange={(e) => setFilterPriority(e.target.value)}>
                <MenuItem value="all">All Priority</MenuItem>
                {Object.values(TICKET_PRIORITY).map(p => (
                  <MenuItem key={p} value={p}>{getPriorityDisplayName(p)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={filterCategory} label="Category" onChange={(e) => setFilterCategory(e.target.value)}>
                <MenuItem value="all">All Categories</MenuItem>
                {Object.values(TICKET_CATEGORY).map(c => (
                  <MenuItem key={c} value={c}>{getCategoryDisplayName(c)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button fullWidth variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`All Tickets (${tickets.length})`} />
        <Tab label={`My Assigned (${tickets.filter(t => t.assignedTo === profile.uid).length})`} />
        <Tab label={`Unassigned (${tickets.filter(t => !t.assignedTo && t.status === TICKET_STATUS.OPEN).length})`} />
        <Tab label={`Urgent/High (${tickets.filter(t => t.priority === TICKET_PRIORITY.URGENT || t.priority === TICKET_PRIORITY.HIGH).length})`} />
      </Tabs>

      {/* Tickets Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'grey.800' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket #</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : getFilteredTickets().length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">No tickets found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              getFilteredTickets()
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{
                      bgcolor: ticket.userResponsePending ? 'action.selected' : 'inherit',
                      '&:hover': { bgcolor: 'grey.700' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {ticket.ticketNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {ticket.subject}
                        </Typography>
                        {ticket.userResponsePending && (
                          <Chip label="User Replied" size="small" color="warning" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ticket.userName}</Typography>
                      <Typography variant="caption" color="text.secondary">{ticket.userEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getCategoryDisplayName(ticket.category)} size="small" />
                    </TableCell>
                    <TableCell>
                      {canManage ? (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={ticket.priority}
                            onChange={(e) => handlePriorityChange(ticket.id, e.target.value)}
                            size="small"
                          >
                            {Object.values(TICKET_PRIORITY).map(p => (
                              <MenuItem key={p} value={p}>{getPriorityDisplayName(p)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          label={getPriorityDisplayName(ticket.priority)}
                          size="small"
                          color={getPriorityColor(ticket.priority)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {canManage ? (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={ticket.status}
                            onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                            size="small"
                          >
                            {Object.values(TICKET_STATUS).map(s => (
                              <MenuItem key={s} value={s}>{getStatusDisplayName(s)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          label={getStatusDisplayName(ticket.status)}
                          size="small"
                          color={getStatusColor(ticket.status)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {ticket.assignedToName || (
                        <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {ticket.createdAt?.toDate ? formatDistanceToNow(ticket.createdAt.toDate(), { addSuffix: true }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewTicket(ticket)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={getFilteredTickets().length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* View Ticket Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6">{selectedTicket?.subject}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedTicket?.ticketNumber} · {selectedTicket?.userEmail}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip
                label={getStatusDisplayName(selectedTicket?.status)}
                size="small"
                color={getStatusColor(selectedTicket?.status)}
              />
              <Chip
                label={getPriorityDisplayName(selectedTicket?.priority)}
                size="small"
                color={getPriorityColor(selectedTicket?.priority)}
                variant="outlined"
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <>
              {/* Ticket Info */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Typography variant="body2">{selectedTicket.userName}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body2">{getCategoryDisplayName(selectedTicket.category)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                  <Typography variant="body2">{selectedTicket.assignedToName || 'Unassigned'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Family Code</Typography>
                  <Typography variant="body2">{selectedTicket.familyCode || '—'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Original Message */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Original Request · {selectedTicket.createdAt?.toDate ? format(selectedTicket.createdAt.toDate(), 'MMM d, yyyy h:mm a') : ''}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedTicket.description}
                </Typography>
              </Paper>

              {/* Conversation */}
              {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Conversation ({selectedTicket.responses.length})
                  </Typography>
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {selectedTicket.responses.map((response, index) => (
                      <ListItem
                        key={response.id || index}
                        sx={{
                          flexDirection: 'column',
                          alignItems: response.isAdminResponse ? 'flex-end' : 'flex-start',
                          px: 0
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '85%',
                            bgcolor: response.isAdminResponse ? 'primary.dark' : 'grey.800',
                            p: 2,
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            {response.authorName} ({response.isAdminResponse ? 'Support' : 'User'}) ·{' '}
                            {response.createdAt?.toDate ? format(response.createdAt.toDate(), 'MMM d, h:mm a') : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {response.message}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Reply Box */}
              {selectedTicket.status !== TICKET_STATUS.CLOSED && canManage && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your response..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                  />
                  <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                    <Button
                      variant="contained"
                      onClick={handleSendReply}
                      disabled={!replyMessage.trim() || submitting}
                      startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                      Send Response
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {canManage && selectedTicket && (
            <>
              <Button
                startIcon={<AssignIcon />}
                onClick={() => setAssignDialogOpen(true)}
              >
                Assign
              </Button>
              {selectedTicket.status !== TICKET_STATUS.RESOLVED && (
                <Button
                  startIcon={<ResolveIcon />}
                  color="success"
                  onClick={() => handleStatusChange(selectedTicket.id, TICKET_STATUS.RESOLVED)}
                >
                  Resolve
                </Button>
              )}
              {selectedTicket.status !== TICKET_STATUS.CLOSED && (
                <Button
                  startIcon={<CloseIcon />}
                  color="error"
                  onClick={() => handleStatusChange(selectedTicket.id, TICKET_STATUS.CLOSED)}
                >
                  Close
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Ticket</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a support agent to assign this ticket to:
          </Typography>
          <List>
            {admins.map(admin => (
              <ListItem
                key={admin.id}
                button
                onClick={() => handleAssignTicket(admin.id, admin.displayName || `${admin.firstName} ${admin.lastName}`)}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: selectedTicket?.assignedTo === admin.id ? 'action.selected' : 'inherit'
                }}
              >
                <Avatar sx={{ mr: 2 }}>{admin.firstName?.charAt(0) || admin.email?.charAt(0)}</Avatar>
                <Box>
                  <Typography variant="body1">
                    {admin.displayName || `${admin.firstName} ${admin.lastName}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {admin.email} · {admin.role}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HelpDeskManagement;
